import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { getNextCursor } from '@/hooks/queryCache/nextCursor';
import { useDeskId } from '@/hooks/useDeskId';
import useUser from '@/hooks/useUser';

import { conversationKeys, API } from './useConversations';

export const usePrefetchConversations = () => {
  const deskId = useDeskId();
  const [urlSearchParams] = useSearchParams();
  const category = urlSearchParams.get('category');
  const { user } = useUser();

  // Kept enabled: staleTime alone fetches them once, and a reconnect refetches only enabled
  // queries, so a disabled list would keep the Mine and Unassigned badges stale.
  useInfiniteQuery({
    queryKey: conversationKeys.byAgent(deskId, 'open', 'newest', user?.user_id),
    queryFn: ({ pageParam }) => {
      return API.listConversations({
        deskId,
        status: 'open',
        agentId: user?.user_id,
        departmentId: '',
        brainId: '',
        sort: '-last_message_at',
        cursor: pageParam.cursor,
      });
    },
    enabled: category !== 'me' && !!deskId && !!user,
    staleTime: Infinity,
    refetchOnMount: 'always',
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
  });

  useInfiniteQuery({
    queryKey: conversationKeys.byStatus(deskId, 'open', 'newest'),
    queryFn: ({ pageParam }) => {
      return API.listConversations({
        deskId,
        status: 'open',
        agentId: '',
        departmentId: '',
        brainId: '',
        sort: '-last_message_at',
        cursor: pageParam.cursor,
      });
    },

    enabled: category !== 'all' && !!deskId,
    staleTime: Infinity,
    refetchOnMount: 'always',
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
  });

  useInfiniteQuery({
    queryKey: conversationKeys.unassigned(deskId, 'open', 'newest'),
    queryFn: ({ pageParam }) => {
      return API.listConversations({
        deskId,
        status: 'open',
        agentId: 'null',
        departmentId: '',
        brainId: 'null',
        sort: '-last_message_at',
        cursor: pageParam.cursor,
      });
    },

    enabled: category !== 'unassigned' && !!deskId,
    staleTime: Infinity,
    refetchOnMount: 'always',
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
  });
};
